import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetMeetingsListQuery, useGetMeetingByIdQuery, type MeetingFull } from '@/redux/internal-services/workspaceApiExtensions';
import { Spinner } from '@/components/UI/Spinner';
import WorkspaceMeetingCard from './WorkspaceMeetingCard';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setMeetingFilter } from '@/redux/slices/uiContextSlice';
import { WORKSPACE_QUERY_OPTIONS } from '@/components/chat/workspaceQueryOptions';
import { useWorkspaceFilterReport, type OnWorkspaceFilterChange } from '@/hooks/useWorkspaceFilterReport';

// ─── Filter chip ────────────────────────────────────────────────────────────────

const FilterChip: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all whitespace-nowrap ${
      active
        ? 'bg-brand-dark/10 border-brand-dark/30 text-brand-dark'
        : 'border-[#EDEDED] bg-ui-subtle text-ui-text2 hover:border-brand-dark/25 hover:text-ui-text'
    }`}
  >
    {label}
  </button>
);

const STATUS_PRESETS = [
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'rescheduled', label: 'Rescheduled' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'no_show', label: 'No Show' },
];

// ─── Component ─────────────────────────────────────────────────────────────────

interface WorkspaceMeetingListProps {
  initialStatus?: string;
  initialMeetingType?: string;
  initialSearch?: string;
  initialOrganizerUserId?: string;
  initialUpdatedByUserId?: string;
  initialExpandedId?: string;
  initialSortBy?: string;
  initialSortDir?: number;
  onSelectMeeting?: (meetingId: string) => void;
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
  /** When set (e.g. opened directly from a notification link), show ONLY this meeting — no
   *  filters, chips, or pagination — instead of the full list. */
  singleMeetingId?: string;
  /** Called when the user leaves single-meeting mode to go back to browsing the full list. */
  onExitSingleMeeting?: () => void;
  /** Workspace mode: report a MANUALLY changed filter so it joins the "⋯" recent-views menu
   *  alongside the agent-driven views. See `useWorkspaceFilterReport`. */
  onFiltersChange?: OnWorkspaceFilterChange;
}

const WorkspaceMeetingList: React.FC<WorkspaceMeetingListProps> = ({
  initialStatus, initialMeetingType, initialSearch, initialOrganizerUserId, initialUpdatedByUserId, initialExpandedId, initialSortBy, initialSortDir, onSelectMeeting, onStartNoteTaker,
  singleMeetingId, onExitSingleMeeting, onFiltersChange,
}) => {
  const { t } = useTranslation();
  const singleMeetingResult = useGetMeetingByIdQuery(singleMeetingId as string, { ...WORKSPACE_QUERY_OPTIONS, skip: !singleMeetingId });
  const [expandedId, setExpandedId] = useState<string | null>(initialExpandedId ?? null);

  useEffect(() => {
    if (initialExpandedId !== undefined) setExpandedId(initialExpandedId ?? null);
  }, [initialExpandedId]);

  const dispatch = useAppDispatch();
  // Seed from Redux shared filter (set by SAM), fall back to props from UXView
  const sharedFilter = useAppSelector((s) => s.uiContext.meetingFilter);
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';

  const [status, setStatus] = useState(initialStatus ?? sharedFilter.status ?? '');
  const [meetingType, setMeetingType] = useState(initialMeetingType ?? sharedFilter.meeting_type ?? '');
  const [search] = useState(initialSearch ?? sharedFilter.search ?? '');
  const [organizerUserId, setOrganizerUserId] = useState(initialOrganizerUserId ?? sharedFilter.organizer_user_id ?? '');
  const [updatedByUserId, setUpdatedByUserId] = useState(initialUpdatedByUserId ?? sharedFilter.updated_by_user_id ?? '');
  const [sortBy] = useState(initialSortBy ?? '');
  const [sortDir] = useState(initialSortDir ?? -1);
  const [page, setPage] = useState(0);
  const limit = 20;

  // Keep local state in sync when SAM applies a new filter from outside
  useEffect(() => {
    if (initialStatus !== undefined) return; // controlled by prop — don't override
    setStatus(sharedFilter.status ?? '');
    setMeetingType(sharedFilter.meeting_type ?? '');
    setOrganizerUserId(sharedFilter.organizer_user_id ?? '');
    setUpdatedByUserId(sharedFilter.updated_by_user_id ?? '');
    setPage(0);
  }, [sharedFilter, initialStatus]);

  // Shared by the query and the recent-views report, so a "⋯" entry replays exactly what's shown.
  const queryArgs = { status: status || undefined, meeting_type: meetingType || undefined, search: search || undefined, organizer_user_id: organizerUserId || undefined, updated_by_user_id: updatedByUserId || undefined, sort_by: sortBy || undefined, sort_dir: sortBy ? sortDir : undefined, skip: page * limit, limit };

  const { data, isLoading, isError, refetch } = useGetMeetingsListQuery(queryArgs, WORKSPACE_QUERY_OPTIONS);

  // Report user-driven filter changes (status/type chips, organizer) to the "⋯" recent-views menu.
  useWorkspaceFilterReport(queryArgs, onFiltersChange);

  // When SAM applies a filter, force a fresh fetch even if the key/args haven't changed
  // (the component may be reused from a prior render with the same filter params).
  useEffect(() => {
    refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedFilter]);

  const meetings: MeetingFull[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const handleStatusChip = (id: string) => {
    const next = status === id ? '' : id;
    setStatus(next);
    setOrganizerUserId('');
    setUpdatedByUserId('');
    setPage(0);
    dispatch(setMeetingFilter({ status: next || undefined, meeting_type: meetingType || undefined, search: search || undefined }));
  };

  const handleCreatedByMeChip = () => {
    const next = organizerUserId === currentUserId ? '' : currentUserId;
    setOrganizerUserId(next);
    setUpdatedByUserId('');
    setStatus('');
    setPage(0);
    dispatch(setMeetingFilter({ organizer_user_id: next || undefined, meeting_type: meetingType || undefined, search: search || undefined }));
  };

  const handleUpdatedByMeChip = () => {
    const next = updatedByUserId === currentUserId ? '' : currentUserId;
    setUpdatedByUserId(next);
    setOrganizerUserId('');
    setStatus('');
    setPage(0);
    dispatch(setMeetingFilter({ updated_by_user_id: next || undefined, meeting_type: meetingType || undefined, search: search || undefined }));
  };

  const clearAll = () => {
    setStatus('');
    setMeetingType('');
    setOrganizerUserId('');
    setUpdatedByUserId('');
    setPage(0);
    dispatch(setMeetingFilter({}));
  };

  const activeCount = [status, meetingType, organizerUserId, updatedByUserId].filter(Boolean).length;

  if (singleMeetingId) {
    return (
      <div className="flex flex-col min-h-full space-y-2">
        {onExitSingleMeeting && (
          <button
            type="button"
            onClick={onExitSingleMeeting}
            className="self-start text-[11px] font-medium text-brand-dark hover:underline"
          >
            {t('meetings.viewAll')}
          </button>
        )}
        {singleMeetingResult.isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
        {singleMeetingResult.isError && <p className="text-center text-[#CF4148] py-8">Failed to load meeting.</p>}
        {singleMeetingResult.data && (
          <div className="grid grid-cols-1 gap-3 pb-2">
            <WorkspaceMeetingCard
              meeting={singleMeetingResult.data}
              onSelect={onSelectMeeting}
              expandedId={onSelectMeeting ? undefined : singleMeetingId}
              onExpand={onSelectMeeting ? undefined : () => {}}
              onStartNoteTaker={onStartNoteTaker}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full space-y-2">
      {/* ── Chip row ── */}
      <div className="overflow-x-auto no-scrollbar -mx-0.5 px-0.5">
        <div className="flex gap-1.5 min-w-max">
          {STATUS_PRESETS.map((preset) => (
            <FilterChip
              key={preset.id}
              label={preset.label}
              active={status === preset.id}
              onClick={() => handleStatusChip(preset.id)}
            />
          ))}
          {!!currentUserId && (
            <FilterChip
              label="Created by Me"
              active={organizerUserId === currentUserId}
              onClick={handleCreatedByMeChip}
            />
          )}
          {!!currentUserId && (
            <FilterChip
              label="Updated by Me"
              active={updatedByUserId === currentUserId}
              onClick={handleUpdatedByMeChip}
            />
          )}
          {activeCount > 0 && (
            <button type="button" onClick={clearAll} className="flex-shrink-0 px-2.5 py-1 text-[11px] text-ui-text3 hover:text-brand-dark transition-colors whitespace-nowrap">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Meeting list ── */}
      {isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
      {isError && <p className="text-center text-[#CF4148] py-8">Failed to load meetings.</p>}
      {!isLoading && !isError && meetings.length === 0 && (
        <p className="text-center text-ui-text3 py-8">No meetings found.</p>
      )}

      {!isLoading && (
        <div className="grid grid-cols-1 gap-3 pb-2">
          {meetings.map((m) => (
            <WorkspaceMeetingCard
              key={m.meeting_id}
              meeting={m}
              onSelect={onSelectMeeting}
              expandedId={onSelectMeeting ? undefined : expandedId}
              onExpand={onSelectMeeting ? undefined : setExpandedId}
              onStartNoteTaker={onStartNoteTaker}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-[#EDEDED] disabled:opacity-40 text-ui-text hover:bg-ui-hover">
            Previous
          </button>
          <span className="text-sm text-ui-text2">{page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-[#EDEDED] disabled:opacity-40 text-ui-text hover:bg-ui-hover">
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkspaceMeetingList;
